import React from 'react';
import {Dropdown, DropdownItem, DropdownMenu, DropdownToggle,} from 'reactstrap';
import {AbstractPopupableProps, AbstractPopupableState} from '@src/@types/components/AbstractPopupable';
import classnames from '@src/helpers/classnames';
import RenderIf from '@src/components/RenderIf/RenderIf';
import Cache from '@src/classes/Cacheable/Cache';
import {randomString} from '@src/helpers/rand';

type AP = AbstractPopupableProps;
type AS = AbstractPopupableState;

export default abstract class AbstractPopupable<P extends AP = AP, S extends AS = AS, SS = any> extends React.PureComponent<P, S, SS> {
    cache = new Cache();

    state = {
        dropdownIsOpen: false,
    } as S;

    protected dropdownToggleId!: string;

    constructor(props: P) {
        super(props);

        this.dropdownToggleId = randomString(12);

        this.toggleDropdownState = this.toggleDropdownState.bind(this);
        this.renderDropdownItem = this.renderDropdownItem.bind(this);
        this.renderDropdownMenu = this.renderDropdownMenu.bind(this);
        this.getDropdownProps = this.getDropdownProps.bind(this);
        this.getDropdownToggleProps = this.getDropdownToggleProps.bind(this);

        if (this.props.isOpen != null) {
            this.state.dropdownIsOpen = this.props.isOpen;
        }
    }

    protected toggleDropdownState(e: React.SyntheticEvent | Event): void {
        const target = e.target as (HTMLElement | null);
        if (
                target &&
                target.closest('[data-dropdown-toggle="' + this.dropdownToggleId + '"]') != null &&
                e instanceof Event
        ) {
            return;
        }

        const dropdownIsOpen = !this.state.dropdownIsOpen;

        this.setState({dropdownIsOpen}, () => {
            if (this.props.onDropdownStateChanged) {
                this.props.onDropdownStateChanged(dropdownIsOpen);
            }
        });
    }

    protected getDropdownToggleProps() {
        return this.props.toggleButtonProps ?? {
            className: 'btn',
            tag: 'button',
            type: 'button',
        };
    }

    protected getDropdownProps() {
        return this.props.dropdownProps ?? {};
    }

    protected getDropdownMenuProps() {
        return this.props.dropdownMenuProps ?? {
            className: 'dropdown-menu-end'
        };
    }

    protected getDropdownItemProps(item: any, index: number) {
        if (this.props.resolveDropdownItemPropsByItem) {
            return this.props.resolveDropdownItemPropsByItem(item, index);
        }

        if (this.props.dropdownItemProps) {
            return this.props.dropdownItemProps;
        }

        return {className: undefined};
    }

    protected getDropdownItems(): any[] {
        return this.props.dropdownItems ?? [];
    }

    protected isActiveDropdownItem(item: any, index: number): boolean {
        if (this.props.isActiveDropdownItem) {
            return this.props.isActiveDropdownItem(item, index);
        }

        return false;
    }

    protected resolveDropdownItemKey(item: any, index: number): string {
        if (this.props.resolveDropdownItemKeyByItem) {
            return this.props.resolveDropdownItemKeyByItem(item, index);
        }

        return `abstract-popupable-${index}-item`;
    }

    protected onDropdownItemClick(e: React.MouseEvent, item: any, index: number): void {
        if (this.props.onDropdownItemClick) {
            this.props.onDropdownItemClick(e, item, index);
        }
    }

    protected renderDropdownToggleContent() {
        if (this.props.renderDropdownToggleContent) {
            return this.props.renderDropdownToggleContent();
        }

        return null;
    }

    protected renderDropdownToggle() {
        const props = this.cache.remember('getDropdownToggleProps', this.getDropdownToggleProps);

        return <DropdownToggle {...props as any} data-dropdown-toggle={this.dropdownToggleId}>
            {this.renderDropdownToggleContent()}
        </DropdownToggle>;
    }

    protected renderDropdownMenuItems() {
        if (this.props.renderDropdownItems) {
            return this.props.renderDropdownItems();
        }

        return this.getDropdownItems().map(this.renderDropdownItem);
    }

    protected renderDropdownMenu() {
        return <DropdownMenu {...this.getDropdownMenuProps()}>
            {this.renderDropdownMenuItems()}
        </DropdownMenu>;
    }

    protected renderDropdownItemContent(item: any, index: number) {
        if (this.props.renderDropdownItemContent) {
            return this.props.renderDropdownItemContent(item, index);
        }

        return null;
    }

    protected renderDropdownItem(item: any, index: number) {
        const itemProps = this.getDropdownItemProps(item, index);

        return <DropdownItem
                key={this.resolveDropdownItemKey(item, index)}
                onClick={e => {
                    this.onDropdownItemClick(e, item, index);
                }}
                {...itemProps}
                className={classnames({
                    [itemProps?.className ?? '']: true,
                    'active': this.isActiveDropdownItem(item, index),
                })}
        >
            {this.renderDropdownItemContent(item, index)}
        </DropdownItem>;
    }

    render() {
        return <Dropdown
                isOpen={this.state.dropdownIsOpen}
                toggle={this.toggleDropdownState}
                {...this.cache.remember('getDropdownProps', this.getDropdownProps)}
                className={this.props.className}
        >
            {this.renderDropdownToggle()}

            <RenderIf
                    expression={this.state.dropdownIsOpen}
                    render={this.renderDropdownMenu}
            />
        </Dropdown>;
    }
}
